import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { PrototypeControls, VariantKey, QualificationState, ListLength, DeviceFrame, ThemeMode } from '../types';

type PrototypeStore = PrototypeControls & {
  setActiveVariant: (v: VariantKey) => void;
  setQualificationState: (q: QualificationState) => void;
  setBalanceValue: (b: number | null) => void;
  setListLength: (l: ListLength) => void;
  setDeviceFrame: (d: DeviceFrame) => void;
  setTheme: (t: ThemeMode) => void;
  setSimulateLoading: (v: boolean) => void;
  setSimulateEmpty: (v: boolean) => void;
  setSimulateOffline: (v: boolean) => void;
  setSimulateError: (v: boolean) => void;
  reset: () => void;
  hydrateFromQuery: (params: URLSearchParams) => void;
  writeToQuery: (params: URLSearchParams) => void;
};

const initialState: PrototypeControls = {
  activeVariant: 'recommended',
  qualificationState: 'low',
  balanceValue: null,
  listLength: 'long',
  deviceFrame: 'android',
  theme: 'light',
  simulateLoading: false,
  simulateEmpty: false,
  simulateOffline: false,
  simulateError: false,
};

export const usePrototypeStore = create<PrototypeStore>()(
  immer((set) => ({
    ...initialState,

    setActiveVariant: (v) =>
      set((state) => {
        state.activeVariant = v;
      }),

    setQualificationState: (q) =>
      set((state) => {
        state.qualificationState = q;
        state.balanceValue = null;
      }),

    setBalanceValue: (b) =>
      set((state) => {
        state.balanceValue = b;
      }),

    setListLength: (l) =>
      set((state) => {
        state.listLength = l;
      }),

    setDeviceFrame: (d) =>
      set((state) => {
        state.deviceFrame = d;
      }),

    setTheme: (t) =>
      set((state) => {
        state.theme = t;
      }),

    setSimulateLoading: (v) =>
      set((state) => {
        state.simulateLoading = v;
      }),

    setSimulateEmpty: (v) =>
      set((state) => {
        state.simulateEmpty = v;
      }),

    setSimulateOffline: (v) =>
      set((state) => {
        state.simulateOffline = v;
      }),

    setSimulateError: (v) =>
      set((state) => {
        state.simulateError = v;
      }),

    reset: () => set(initialState),

    hydrateFromQuery: (params) =>
      set((state) => {
        const variant = params.get('variant') as VariantKey | null;
        if (variant && isVariantKey(variant)) state.activeVariant = variant;

        const q = params.get('state') as QualificationState | null;
        if (q && isQualificationState(q)) state.qualificationState = q;

        const list = params.get('list') as ListLength | null;
        if (list && isListLength(list)) state.listLength = list;

        const device = params.get('device') as DeviceFrame | null;
        if (device && isDeviceFrame(device)) state.deviceFrame = device;

        const theme = params.get('theme') as ThemeMode | null;
        if (theme && isThemeMode(theme)) state.theme = theme;

        const balance = params.get('balance');
        if (balance !== null) {
          const parsed = Number(balance);
          if (!Number.isNaN(parsed)) state.balanceValue = parsed;
        }
      }),

    writeToQuery: (params) => {
      set((state) => {
        params.set('variant', state.activeVariant);
        params.set('state', state.qualificationState);
        params.set('list', state.listLength);
        params.set('device', state.deviceFrame);
        params.set('theme', state.theme);
        if (state.balanceValue !== null) {
          params.set('balance', String(state.balanceValue));
        } else {
          params.delete('balance');
        }
      });
    },
  }))
);

function isVariantKey(v: string): v is VariantKey {
  return ['recommended', 'A', 'B', 'C', 'D', 'E', 'F'].includes(v);
}

function isQualificationState(q: string): q is QualificationState {
  return ['unqualified', 'low', 'negative', 'predicted-low'].includes(q);
}

function isListLength(l: string): l is ListLength {
  return ['empty', 'short', 'long', 'mixed'].includes(l);
}

function isDeviceFrame(d: string): d is DeviceFrame {
  return ['android', 'iphone'].includes(d);
}

function isThemeMode(t: string): t is ThemeMode {
  return ['light', 'dark'].includes(t);
}
